import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Bot, User, Sparkles, X, Maximize2, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import ReactMarkdown from 'react-markdown';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser || !isOpen) return;

    // Charger l'historique des messages
    const loadMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('ai_conversations')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('timestamp', { ascending: true })
          .limit(20);

        if (error) throw error;
        
        if (data && data.length > 0) {
          setMessages(data.map(msg => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp)
          })));
        } else {
          // Message de bienvenue
          const welcomeMessage: Message = {
            id: 'welcome',
            role: 'assistant',
            content: "Bonjour ! Je suis votre assistant énergétique personnel. Comment puis-je vous aider aujourd'hui ? Vous pouvez me poser des questions sur votre type énergétique, demander des conseils pour améliorer votre bien-être, ou simplement discuter de votre journée.",
            timestamp: new Date()
          };
          setMessages([welcomeMessage]);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      }
    };

    loadMessages();
  }, [currentUser, isOpen]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || !currentUser) return;
    
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Enregistrer le message de l'utilisateur
      await supabase
        .from('ai_conversations')
        .insert({
          user_id: currentUser.id,
          role: 'user',
          content: userMessage.content,
          timestamp: userMessage.timestamp.toISOString()
        });
      
      // Simuler une réponse de l'IA (à remplacer par un appel API réel)
      setTimeout(async () => {
        // Générer une réponse basée sur le message de l'utilisateur
        let response = '';
        
        if (input.toLowerCase().includes('énergie') || input.toLowerCase().includes('energy')) {
          response = "Votre énergie est influencée par de nombreux facteurs, notamment votre type Human Design. Avez-vous remarqué des fluctuations récentes dans votre niveau d'énergie ? Je vous suggère de suivre régulièrement votre état énergétique et d'identifier les activités qui vous dynamisent ou vous épuisent.";
        } else if (input.toLowerCase().includes('exercice') || input.toLowerCase().includes('exercise')) {
          response = "Les exercices adaptés à votre type énergétique peuvent faire une grande différence. Pour les Generators et Manifesting Generators, des activités physiques régulières sont bénéfiques. Les Projectors devraient privilégier des exercices moins intenses mais plus ciblés. Les Manifestors peuvent bénéficier d'activités qui libèrent leur énergie créative.";
        } else if (input.toLowerCase().includes('sommeil') || input.toLowerCase().includes('sleep')) {
          response = "Le sommeil est crucial pour recharger votre énergie. Essayez d'établir une routine régulière et créez un environnement propice au repos. La qualité du sommeil est souvent plus importante que la quantité, surtout pour les Reflectors qui sont particulièrement sensibles aux cycles lunaires.";
        } else if (input.toLowerCase().includes('stress') || input.toLowerCase().includes('anxiété') || input.toLowerCase().includes('anxiety')) {
          response = "Le stress peut sérieusement affecter votre équilibre énergétique. Des techniques de respiration profonde, la méditation ou des pauses régulières peuvent vous aider à gérer le stress. Rappelez-vous que chaque type énergétique réagit différemment au stress - connaître votre type peut vous aider à développer des stratégies d'adaptation efficaces.";
        } else if (input.toLowerCase().includes('alimentation') || input.toLowerCase().includes('food') || input.toLowerCase().includes('nutrition')) {
          response = "L'alimentation joue un rôle important dans votre équilibre énergétique. Essayez d'observer comment différents aliments affectent votre énergie. Généralement, une alimentation riche en fruits, légumes et protéines de qualité est bénéfique pour tous les types énergétiques.";
        } else {
          response = "Merci pour votre message. Je suis là pour vous aider à comprendre et optimiser votre énergie selon votre type Human Design. N'hésitez pas à me poser des questions spécifiques sur votre bien-être énergétique, vos symptômes, ou des conseils adaptés à votre type.";
        }
        
        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
        
        // Enregistrer la réponse de l'assistant
        await supabase
          .from('ai_conversations')
          .insert({
            user_id: currentUser.id,
            role: 'assistant',
            content: assistantMessage.content,
            timestamp: assistantMessage.timestamp.toISOString()
          });
      }, 1500);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <>
      {/* Bouton flottant */}
      {!isOpen && (
        <motion.button
          className="fixed bottom-6 right-6 p-4 bg-[#54075B] text-white rounded-full shadow-lg z-50"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(true)}
        >
          <Bot className="w-6 h-6" />
        </motion.button>
      )}
      
      {/* Fenêtre de chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ 
              opacity: 1,
              y: 0,
              scale: 1,
              height: isMinimized ? 'auto' : '500px'
            }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 w-80 sm:w-96 bg-white rounded-lg shadow-xl z-50 overflow-hidden"
          >
            <CardHeader className="p-3 flex flex-row items-center justify-between bg-[#54075B] text-white">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5" />
                <h3 className="font-semibold">Assistant Énergétique</h3>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleMinimize}
                  className="p-1 rounded-full hover:bg-white/20"
                >
                  {isMinimized ? (
                    <Maximize2 className="w-4 h-4" />
                  ) : (
                    <Minimize2 className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-full hover:bg-white/20"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardHeader>
            
            <AnimatePresence>
              {!isMinimized && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                >
                  <CardContent className="p-0 flex flex-col h-[calc(500px-56px)]">
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] p-3 rounded-lg ${
                              message.role === 'user'
                                ? 'bg-[#54075B] text-white'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {message.role === 'assistant' ? (
                                <Bot className="w-4 h-4" />
                              ) : (
                                <User className="w-4 h-4" />
                              )}
                              <span className="text-xs opacity-75">
                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="text-sm">
                              <ReactMarkdown>{message.content}</ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="max-w-[80%] p-3 rounded-lg bg-gray-100">
                            <div className="flex items-center gap-2">
                              <Bot className="w-4 h-4 text-gray-600" />
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                    
                    <div className="p-3 border-t">
                      <div className="flex gap-2">
                        <Textarea
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Posez une question..."
                          className="min-h-[60px] resize-none"
                        />
                        <Button
                          onClick={sendMessage}
                          disabled={!input.trim() || isLoading}
                          className="h-auto"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex items-center justify-center mt-2">
                        <Sparkles className="w-3 h-3 text-purple-400 mr-1" />
                        <span className="text-xs text-gray-500">
                          Propulsé par IA
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}